import { capitalizeString, sortAsc } from '@strata/helper-functions';
import { FilterCategory } from '@strata/react-filter-pane';

import {
    Conversation,
    ConversationMessage,
    findConversations,
    getConversationFacts,
    getConversationFilterOptions,
    registerCurrentUser,
} from 'api/userSupportAPI';
import { handleError } from 'services/Error';
import {
    FMH_UPN,
    formatNameMaintainCase,
    mapConversation,
    mapMessage,
    MappedMessage,
} from 'services/userSupportHelpers';
import { AppThunk } from 'store';
import { Grids } from 'store/grids/gridsSlice';
import {
    setAdditionalPageData,
    setConversationFacts,
    setConversationFilterCategories,
    setCurrentPage,
    setPageData,
    updateConversationMessages,
    updateReferralConversations,
    updateUserProfile,
} from 'store/userSupport/userSupportSlice';

export const registerUser =
    ({ details }: { details: { first_name?: string; last_name?: string } }): AppThunk =>
    async (dispatch) => {
        try {
            const userProfile = await registerCurrentUser({ details });
            dispatch(
                updateUserProfile({
                    upn: userProfile.upn,
                    firstName: userProfile.first_name,
                    lastName: userProfile.last_name,
                }),
            );
        } catch (err) {
            handleError('An error occurred retrieving user details.', err as Error);
        }
    };

export const updateConversations =
    (conversations: Conversation[]): AppThunk =>
    (dispatch) => {
        const mappedConversations = conversations.map(mapConversation);
        const conversationsList = mappedConversations.reduce(
            (prev, curr) => ({ ...prev, [curr.conversationId]: curr }),
            {},
        );
        dispatch(
            updateReferralConversations({
                conversations: conversationsList,
            }),
        );
    };

export interface SocketMessagesPayload {
    conversation_id?: string;
    messages: ConversationMessage[];
}
export const updateMessages =
    (payload: SocketMessagesPayload): AppThunk =>
    (dispatch) => {
        const mappedMessages = payload.messages.map(mapMessage);
        const conversationId = payload.conversation_id;
        const messagesList = mappedMessages.reduce<Record<string, MappedMessage>>(
            (prev, curr) => ({ ...prev, [curr.messageId]: curr }),
            {},
        );
        if (conversationId) {
            dispatch(updateConversationMessages({ conversationId, messages: messagesList }));
        }
    };

export const fetchConversations =
    (searchTerm?: string): AppThunk =>
    async (dispatch, getState) => {
        try {
            const page = getState().userSupport.pageData.currentPage;
            const { activeFilters } = getState().grids[Grids.CONVERSATIONS];

            const checkLength = (array: string[] = []) => (array.length > 0 ? array : undefined);

            const additionalFilters = {
                conversation_status: checkLength(activeFilters?.conversation_status),
                conversation_type: checkLength(activeFilters?.conversation_type),
                supplier_npi: checkLength(activeFilters?.supplier_npi),
                is_unread: checkLength(activeFilters?.is_unread),
                created_by_upn: checkLength(activeFilters?.created_by_upn),
                short_title: checkLength(activeFilters?.short_title),
            };

            const response = await findConversations({
                page,
                searchTerm,
                additionalFilters,
            });

            const { conversations, count } = response;

            if (page === 0) {
                dispatch(
                    setAdditionalPageData({
                        conversationCount: count,
                    }),
                );
            }

            dispatch(setPageData({ page, conversations: conversations.map(mapConversation) }));
        } catch (err) {
            handleError('An error occurred while retrieving conversations.', err as Error);
        }
    };

export const fetchConversationFacts =
    (conversationId: string): AppThunk =>
    async (dispatch) => {
        try {
            const response = await getConversationFacts({ conversationId });
            dispatch(setConversationFacts({ conversationId, facts: response }));
        } catch (error) {
            handleError('An error occurred while retrieving conversation facts.', error as Error);
        }
    };

export const fetchConversationFilterCategories = (): AppThunk => async (dispatch) => {
    try {
        const response = await getConversationFilterOptions();

        const filterOptions: Record<string, { text: string; value: string | boolean }[]> = {
            ...response,
            conversation_status: response.conversation_status.map((item) => ({
                text: capitalizeString(item.value as string),
                value: item.value,
            })),
            conversation_type: response.conversation_type.map((item) => ({
                text: capitalizeString(item.value as string),
                value: item.value,
            })),
            is_unread: response.is_unread.map((item) => ({
                text: item.value ? 'Unread' : 'Read',
                value: item.value,
            })),
            created_by_upn: response.created_by_upn.map(({ value, details }) => ({
                text: formatNameMaintainCase(
                    details?.created_by_first_name,
                    details?.created_by_last_name,
                ),
                value,
            })),
        };

        const filterCategories = [
            { key: 'conversation_status', title: 'Conversation Status' },
            { key: 'is_unread', title: 'Read' },
            { key: 'created_by_upn', title: 'Created By' },
            { key: 'short_title', title: 'Conversation Topic' },
        ].map((category) => {
            let values = filterOptions[category.key]?.sort((a, b) =>
                sortAsc(a.text ?? a.value, b.text ?? b.value),
            );
            if (category.key === 'created_by_upn') {
                values = [
                    {
                        text: 'Created by Me',
                        value: 'created_by_me',
                    },
                    {
                        text: 'FMH Support',
                        value: FMH_UPN,
                    },
                    ...values,
                ];
            }

            return {
                ...category,
                customValues: values,
            } as FilterCategory<Conversation>;
        });

        dispatch(setConversationFilterCategories(filterCategories));
    } catch (err) {
        handleError(
            'An error occurred while retrieving conversation filter options.',
            err as Error,
        );
    }
};

export const updatePage =
    (currentPage: number): AppThunk =>
    (dispatch) => {
        dispatch(setCurrentPage({ currentPage }));
    };
